/**
 * NOVA Monoproducto · theme.js
 * Núcleo del tema, sin dependencias (~5 KB gzip). Se carga con defer.
 *
 * ÍNDICE
 *  1. Utilidades y formato de moneda
 *  2. Estado del producto (variante + cantidad + oferta) y render de precios
 *  3. Compra: añade al carrito real de Shopify y va al pago / carrito
 *  4. Cabecera (scroll + menú móvil)
 *  5. Scroll reveal y parallax del hero
 *  6. Acordeones (FAQ) y hotspots
 *  7. <lazy-video>: vídeos que no cargan hasta el click
 *  8. Carrito y editor de temas
 *
 * Casi todo usa delegación de eventos en document: funciona igual cuando el editor de
 * temas vuelve a pintar una sección.
 */
(function () {
  'use strict';

  var theme = (window.theme = window.theme || {});
  var routes = theme.routes || {};
  var strings = theme.strings || {};

  /* ------------------------------------------------------------------------
   * 1. Utilidades
   * --------------------------------------------------------------------- */
  function $(selector, context) {
    return (context || document).querySelector(selector);
  }

  function $$(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  function motionEnabled() {
    return document.documentElement.classList.contains('motion');
  }

  /**
   * Formatea céntimos con el formato de moneda de la tienda (shop.money_format).
   * Soporta todos los formatos de Shopify: {{amount}}, {{amount_with_comma_separator}}, etc.
   */
  theme.formatMoney = function (cents, format) {
    var pattern = /\{\{\s*(\w+)\s*\}\}/;
    var template = format || theme.moneyFormat || '{{amount}}';

    function withSeparators(value, precision, thousands, decimal) {
      var fixed = (value / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var match = template.match(pattern);
    var output;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        output = withSeparators(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        output = withSeparators(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        output = withSeparators(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        output = withSeparators(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        output = withSeparators(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        output = withSeparators(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        output = withSeparators(cents, 2, ' ', '.');
        break;
      default:
        output = withSeparators(cents, 2, ',', '.');
    }
    return template.replace(pattern, output);
  };

  var toastTimer;
  theme.toast = function (message) {
    var toast = $('#Toast');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.hidden = true;
    }, 6000);
  };

  /* ------------------------------------------------------------------------
   * 2. Estado del producto
   *    Una sola fuente de verdad para variante, cantidad y oferta. Cualquier elemento
   *    con estos atributos se actualiza solo, esté en la sección que esté:
   *      [data-unit-price] [data-unit-compare] [data-unit-badge]   → precio por unidad (hero)
   *      [data-total-price] [data-total-compare] [data-total-badge] [data-savings] → total de la oferta
   *      [data-buy] [data-buy-label] [data-availability] [data-variant-input]
   * --------------------------------------------------------------------- */
  var product = null;
  var state = { variant: null, quantity: 1, pct: 0, code: '' };

  function setHidden(el, hidden) {
    if (el) el.hidden = hidden;
  }

  function setMoney(elements, cents) {
    elements.forEach(function (el) {
      el.innerHTML = theme.formatMoney(cents);
    });
  }

  /** Precio de un pack: unidades × precio × (100 − %) / 100, redondeado al céntimo. */
  function packPrice(variant, quantity, pct) {
    var compareUnit = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
    var total = Math.round((variant.price * quantity * (100 - pct)) / 100);
    var compare = compareUnit * quantity;
    var savings = Math.max(0, compare - total);
    return {
      total: total,
      compare: compare,
      savings: savings,
      savingsPct: compare > 0 ? Math.round((savings * 100) / compare) : 0,
      perUnit: Math.round(total / quantity)
    };
  }

  function render() {
    var variant = state.variant;
    if (!variant) return;

    var unitOnSale = variant.compare_at_price > variant.price;
    var unitPct = unitOnSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
    var pack = packPrice(variant, state.quantity, state.pct);

    // Precio por unidad (hero)
    setMoney($$('[data-unit-price]'), variant.price);
    $$('[data-unit-compare]').forEach(function (el) {
      el.innerHTML = theme.formatMoney(variant.compare_at_price);
      el.hidden = !unitOnSale;
    });
    $$('[data-unit-compare-label]').forEach(function (el) {
      el.hidden = !unitOnSale;
    });
    $$('[data-unit-badge]').forEach(function (el) {
      el.textContent = '−' + unitPct + '%';
      el.hidden = !unitOnSale;
    });

    // Total de la oferta (ofertas, sticky, CTA final)
    setMoney($$('[data-total-price]'), pack.total);
    $$('[data-total-compare]').forEach(function (el) {
      el.innerHTML = theme.formatMoney(pack.compare);
      el.hidden = pack.savings === 0;
    });
    $$('[data-total-badge]').forEach(function (el) {
      el.textContent = '−' + pack.savingsPct + '%';
      el.hidden = pack.savingsPct === 0;
    });
    $$('[data-savings]').forEach(function (el) {
      el.innerHTML = 'Ahorras ' + theme.formatMoney(pack.savings);
      el.hidden = pack.savings === 0;
    });

    // Tarjetas de oferta: precio, tachado, precio por unidad y % de ahorro de cada una
    $$('input[data-offer]').forEach(function (input) {
      var card = input.closest('.product-offer-card');
      if (!card) return;
      var qty = parseInt(input.dataset.qty, 10) || 1;
      var offer = packPrice(variant, qty, parseFloat(input.dataset.pct) || 0);
      var price = $('[data-offer-price]', card);
      var compare = $('[data-offer-compare]', card);
      var unit = $('[data-offer-unit]', card);
      var save = $('[data-offer-save]', card);
      if (price) price.innerHTML = theme.formatMoney(offer.total);
      if (compare) {
        compare.innerHTML = theme.formatMoney(offer.compare);
        compare.hidden = offer.savings === 0;
      }
      if (unit) {
        unit.innerHTML = theme.formatMoney(offer.perUnit) + ' / ud.';
        unit.hidden = qty === 1;
      }
      if (save) {
        save.textContent = (input.dataset.saveLabel || 'AHORRA [X]%').replace('[X]', offer.savingsPct);
        save.hidden = offer.savingsPct === 0;
      }
      card.classList.toggle('is-selected', input.checked);
    });

    // Formulario real, botones y disponibilidad
    $$('[data-variant-input]').forEach(function (input) {
      input.value = variant.id;
    });
    $$('[data-qty-input]').forEach(function (input) {
      input.value = state.quantity;
    });
    $$('[data-buy]').forEach(function (button) {
      button.disabled = !variant.available;
      var label = $('[data-buy-label]', button);
      if (label) label.textContent = variant.available ? label.dataset.label : label.dataset.soldOut || strings.soldOut;
    });
    $$('[data-availability]').forEach(function (el) {
      var text = $('[data-availability-text]', el);
      var low = variant.available && variant.low_stock;
      el.classList.toggle('availability--out', !variant.available);
      el.classList.toggle('availability--low', !!low);
      if (!text) return;
      if (!variant.available) text.textContent = el.dataset.textOut;
      else if (low) text.textContent = (el.dataset.textLow || '').replace('[N]', variant.low_stock);
      else text.textContent = el.dataset.textIn;
    });
    variant.options.forEach(function (value, index) {
      $$('[data-option-value="' + index + '"]').forEach(function (el) {
        el.textContent = value;
      });
    });

    document.dispatchEvent(
      new CustomEvent('product:change', { detail: { variant: variant, quantity: state.quantity, pack: pack } })
    );
  }

  /** Marca la oferta cuyo nº de unidades coincide con la cantidad; si ninguna coincide, sin descuento. */
  function syncOfferFromQuantity() {
    var matched = false;
    $$('input[data-offer]').forEach(function (input) {
      var isMatch = !matched && parseInt(input.dataset.qty, 10) === state.quantity;
      input.checked = isMatch;
      if (isMatch) {
        matched = true;
        state.pct = parseFloat(input.dataset.pct) || 0;
        state.code = input.dataset.code || '';
      }
    });
    if (!matched) {
      state.pct = 0;
      state.code = '';
    }
  }

  function setQuantity(value) {
    state.quantity = Math.min(99, Math.max(1, parseInt(value, 10) || 1));
    syncOfferFromQuantity();
    render();
  }

  function selectOffer(input) {
    state.quantity = parseInt(input.dataset.qty, 10) || 1;
    state.pct = parseFloat(input.dataset.pct) || 0;
    state.code = input.dataset.code || '';
    render();
  }

  function selectVariantFromOptions() {
    var picker = $('[data-variant-picker]');
    if (!picker || !product) return;
    var chosen = [];
    $$('input[data-option-index]:checked', picker).forEach(function (input) {
      chosen[parseInt(input.dataset.optionIndex, 10)] = input.value;
    });
    var match = product.variants.filter(function (variant) {
      return variant.options.every(function (value, index) {
        return value === chosen[index];
      });
    })[0];
    if (!match) return;
    state.variant = match;
    render();

    // Muestra la imagen de la variante en la galería del hero
    if (match.image_position) {
      var slide = $('[data-image-position="' + match.image_position + '"]');
      var gallery = slide && slide.closest('carousel-slider');
      if (gallery && typeof gallery.goToSlide === 'function') gallery.goToSlide(slide);
    }
  }

  theme.initProduct = function () {
    var dataEl = $('#ProductData');
    if (!dataEl) return;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    state.variant =
      product.variants.filter(function (variant) {
        return variant.id === product.selected;
      })[0] || product.variants[0];

    var checkedOffer = $('input[data-offer]:checked');
    if (checkedOffer) {
      state.quantity = parseInt(checkedOffer.dataset.qty, 10) || 1;
      state.pct = parseFloat(checkedOffer.dataset.pct) || 0;
      state.code = checkedOffer.dataset.code || '';
    } else {
      state.quantity = 1;
      state.pct = 0;
      state.code = '';
    }
    render();
  };

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (target.matches('input[data-option-index]')) selectVariantFromOptions();
    else if (target.matches('input[data-offer]')) selectOffer(target);
    else if (target.matches('[data-qty-input]') && !target.closest('[data-cart-form]')) setQuantity(target.value);
  });

  document.addEventListener('click', function (event) {
    var stepper = event.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!stepper) return;
    var wrapper = stepper.closest('[data-quantity]');
    var input = wrapper && $('input', wrapper);
    if (!input) return;
    var next = (parseInt(input.value, 10) || 1) + (stepper.hasAttribute('data-qty-plus') ? 1 : -1);
    var min = parseInt(input.min, 10);
    next = Math.max(isNaN(min) ? 1 : min, Math.min(99, next));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------------
   * 3. Compra (carrito y checkout REALES de Shopify)
   *    - Añade la variante y cantidad elegidas con la Cart AJAX API.
   *    - "Ir directo al pago": sustituye el carrito por la oferta elegida, como el botón
   *      nativo "Comprar ahora", y redirige a /checkout.
   *    - Si la oferta tiene código de descuento, lo aplica con /discount/CODIGO?redirect=…
   *    - Sin JavaScript el formulario se envía de forma nativa a /cart/add.
   * --------------------------------------------------------------------- */
  var buying = false;

  function setBuying(isBuying) {
    buying = isBuying;
    $$('[data-buy]').forEach(function (button) {
      button.classList.toggle('is-loading', isBuying);
      button.setAttribute('aria-busy', String(isBuying));
    });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    });
  }

  theme.buy = function () {
    if (buying || !state.variant || !state.variant.available) return;
    setBuying(true);

    var toCheckout = theme.buyAction === 'checkout';
    var clear = toCheckout ? post(routes.cartClear + '.js') : Promise.resolve();

    clear
      .then(function () {
        return post(routes.cartAdd + '.js', { items: [{ id: state.variant.id, quantity: state.quantity }] });
      })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || strings.addError);
          return data;
        });
      })
      .then(function () {
        var destination = toCheckout ? '/checkout' : routes.cart;
        window.location.href = state.code
          ? '/discount/' + encodeURIComponent(state.code) + '?redirect=' + encodeURIComponent(destination)
          : destination;
      })
      .catch(function (error) {
        setBuying(false);
        theme.toast((error && error.message) || strings.addError);
      });
  };

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-buy]');
    if (!button || !product) return;
    event.preventDefault();
    theme.buy();
  });

  document.addEventListener('submit', function (event) {
    if (!event.target.matches('[data-product-form]') || !product) return;
    // Los botones de pago exprés de Shopify gestionan su propio envío
    if (event.submitter && event.submitter.closest('.shopify-payment-button')) return;
    event.preventDefault();
    theme.buy();
  });

  // Al volver atrás desde el checkout (bfcache) se quita el estado de carga
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) setBuying(false);
  });

  /* ------------------------------------------------------------------------
   * 4. Cabecera: transparente → sólida, y menú móvil accesible
   * --------------------------------------------------------------------- */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    // Un "centinela" de 1 px al inicio de la página evita escuchar el evento scroll
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:24px;pointer-events:none;';
    document.body.prepend(sentinel);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        header.classList.toggle('is-scrolled', !entries[0].isIntersecting);
      }).observe(sentinel);
    }
  }

  function closeMenu() {
    var toggle = $('[data-menu-toggle]');
    var menu = $('[data-mobile-menu]');
    if (!toggle || !menu || menu.hidden) return;
    menu.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Abrir menú');
    document.documentElement.classList.remove('menu-open');
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-menu-toggle]');
    if (toggle) {
      var menu = $('[data-mobile-menu]');
      if (!menu) return;
      if (menu.hidden) {
        menu.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-label', 'Cerrar menú');
        document.documentElement.classList.add('menu-open');
      } else {
        closeMenu();
      }
      return;
    }
    // Cierra al pulsar un enlace del menú o fuera de la cabecera
    if (event.target.closest('[data-mobile-menu] a') || !event.target.closest('[data-header]')) closeMenu();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var menu = $('[data-mobile-menu]');
    if (menu && !menu.hidden) {
      closeMenu();
      var toggle = $('[data-menu-toggle]');
      if (toggle) toggle.focus();
    }
  });

  /* ------------------------------------------------------------------------
   * 5. Scroll reveal y parallax
   * --------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveal(root) {
    var items = $$('.reveal:not(.is-visible)', root);
    if (!items.length) return;

    if (!motionEnabled() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
    }
    items.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /** Parallax ligero del producto del hero: solo escritorio, solo mientras el hero está en pantalla. */
  function initParallax() {
    var stage = $('[data-parallax]');
    if (!stage || !motionEnabled() || !('IntersectionObserver' in window)) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    var ticking = false;
    function update() {
      ticking = false;
      stage.style.setProperty('--parallax', Math.min(40, window.scrollY * 0.07).toFixed(1) + 'px');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) window.addEventListener('scroll', onScroll, { passive: true });
      else window.removeEventListener('scroll', onScroll);
    }).observe(stage);
  }

  /* ------------------------------------------------------------------------
   * 6. Acordeones y hotspots
   * --------------------------------------------------------------------- */
  // <details> animado: funciona sin JS; con JS se anima la altura (Web Animations API)
  document.addEventListener('click', function (event) {
    var summary = event.target.closest('[data-accordion] > summary');
    if (!summary) return;
    var details = summary.parentElement;
    var content = $('[data-accordion-content]', details);
    if (!content || !content.animate || !motionEnabled()) return;

    event.preventDefault();
    if (details.dataset.animating) return;
    details.dataset.animating = 'true';
    var options = { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
    var animation;

    if (details.open) {
      details.classList.add('is-closing');
      animation = content.animate(
        { height: [content.offsetHeight + 'px', '0px'], opacity: [1, 0] },
        options
      );
      animation.onfinish = animation.oncancel = function () {
        details.open = false;
        details.classList.remove('is-closing');
        delete details.dataset.animating;
      };
    } else {
      details.open = true;
      animation = content.animate(
        { height: ['0px', content.offsetHeight + 'px'], opacity: [0, 1] },
        options
      );
      animation.onfinish = animation.oncancel = function () {
        delete details.dataset.animating;
      };
    }
  });

  function activateHotspot(root, index) {
    $$('[data-hotspot]', root).forEach(function (dot) {
      var active = dot.dataset.hotspot === String(index);
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-expanded', String(active));
    });
    $$('[data-hotspot-panel]', root).forEach(function (panel) {
      var active = panel.dataset.hotspotPanel === String(index);
      panel.classList.toggle('is-active', active);
      var trigger = $('[data-hotspot-trigger]', panel);
      if (trigger) trigger.setAttribute('aria-expanded', String(active));
    });
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-hotspot], [data-hotspot-trigger]');
    if (!trigger) return;
    var root = trigger.closest('[data-hotspots]');
    if (!root) return;
    var index = trigger.hasAttribute('data-hotspot') ? trigger.dataset.hotspot : trigger.dataset.hotspotTrigger;
    activateHotspot(root, index);
  });

  /* ------------------------------------------------------------------------
   * 7. <lazy-video>
   *    El <video>/<iframe> está dentro de un <template>: no descarga nada hasta el click.
   *    Autoplay opcional (data-autoplay): silenciado, solo en pantalla, nunca con ahorro
   *    de datos ni con "reducir movimiento".
   * --------------------------------------------------------------------- */
  if (!customElements.get('lazy-video')) {
    customElements.define(
      'lazy-video',
      class LazyVideo extends HTMLElement {
        connectedCallback() {
          this.template = this.querySelector('template');
          this.button = this.querySelector('[data-video-play]');
          if (!this.template) return;

          if (this.button) this.button.addEventListener('click', () => this.play(true));

          const saveData = navigator.connection && navigator.connection.saveData;
          if (this.hasAttribute('data-autoplay') && motionEnabled() && !saveData && 'IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              (entries) => {
                if (this.userStarted) return;
                if (entries[0].isIntersecting) this.play(false);
                else if (this.video) this.video.pause();
              },
              { threshold: 0.6 }
            );
            this.observer.observe(this);
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        load() {
          if (this.media) return;
          this.media = this.template.content.firstElementChild.cloneNode(true);
          this.video = this.media.tagName === 'VIDEO' ? this.media : this.media.querySelector('video');
          this.appendChild(this.media);
        }

        play(byUser) {
          this.load();
          if (byUser) {
            this.userStarted = true;
            this.classList.add('is-active');
            // Pausa cualquier otro vídeo en reproducción
            document.querySelectorAll('lazy-video video').forEach((other) => {
              if (other !== this.video) other.pause();
            });
          }
          if (!this.video) return;
          this.video.muted = !byUser;
          this.video.loop = !byUser;
          this.video.controls = byUser;
          this.video.playsInline = true;
          const attempt = this.video.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
          if (byUser) this.video.focus();
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
   * 8. Carrito y editor de temas
   * --------------------------------------------------------------------- */
  // Página de carrito: al cambiar una cantidad se envía el formulario (actualiza en servidor)
  document.addEventListener('change', function (event) {
    var form = event.target.closest('[data-cart-form]');
    if (form && event.target.matches('input[type="number"]')) form.submit();
  });

  function init() {
    initHeader();
    initReveal(document);
    initParallax();
    theme.initProduct();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Editor de temas: al repintar una sección se reinician sus animaciones y datos
  document.addEventListener('shopify:section:load', function (event) {
    $$('.reveal', event.target).forEach(function (el) {
      el.classList.add('is-visible');
    });
    theme.initProduct();
  });

  document.addEventListener('shopify:block:select', function (event) {
    var hotspot = event.target.closest('[data-hotspot]');
    if (hotspot) activateHotspot(hotspot.closest('[data-hotspots]'), hotspot.dataset.hotspot);
  });
})();
